import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Moon, Sun, Globe, RefreshCw } from 'lucide-react';
import ArcadeMark from '../components/ArcadeMark';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Login = () => {
  const { login, t, theme, toggleTheme, language, setLanguage, API, tenantName } = useApp();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ login: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotId, setForgotId] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [captcha, setCaptcha] = useState(null); // { token, question }
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  const loadCaptcha = async () => {
    setCaptchaAnswer('');
    try {
      const r = await axios.get(`${API}/auth/captcha`);
      setCaptcha(r.data);
    } catch (e) {
      setCaptcha(null);
    }
  };

  const openForgot = () => {
    setShowForgot(true);
    loadCaptcha();
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotId.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    if (!captchaAnswer.trim()) {
      toast.error(t('auth.captchaLabel'));
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        login_or_email: forgotId.trim(),
        base_url: window.location.origin,
        captcha_token: captcha?.token,
        captcha_answer: captchaAnswer.trim(),
      });
      toast.success(t('auth.resetLinkSent'));
      setShowForgot(false);
      setForgotId('');
    } catch (error) {
      // Captcha consommé (usage unique) : on en recharge un nouveau
      toast.error(error.response?.data?.detail || t('auth.captchaError'));
      loadCaptcha();
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.login || !formData.password) {
      toast.error(t('errors.requiredField'));
      return;
    }
    setLoading(true);
    try {
      await login(formData);
      navigate('/dashboard');
    } catch (error) {
      // handled by interceptors / silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden arcade-field grid-texture scanlines crt-vignette flex items-center justify-center p-4">
      {/* Theme / language toggles */}
      <div className="fixed top-4 right-4 z-20 flex gap-2">
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="login-theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="login-language-toggle">
              <Globe className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="login-lang-en">
              {t('settings.english')} {language === 'en' && '✓'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="login-lang-fr">
              {t('settings.french')} {language === 'fr' && '✓'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="relative z-10 w-full max-w-md animate-fade-in-up" data-testid="login-card">
        {/* ONE cabinet: illuminated marquee header + underlight strip + control panel */}
        <div className="cabinet-shell">
          {/* MARQUEE header band */}
          <div className="cabinet-marquee">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-background/60 border border-primary/40 text-primary glow-amber animate-flicker mb-3">
              <ArcadeMark className="w-8 h-8" />
            </span>
            <p className="eyebrow text-[10px] text-primary/80 mb-1">Arcade Fleet · Stock Manager</p>
            <h1 className="font-display font-bold text-4xl tracking-[0.14em] leading-none">
              <span className="chrome-text">{(tenantName || 'ARCADE').toUpperCase()}</span>
            </h1>
            <div className="mt-2 flex items-center justify-center gap-2">
              <span className="h-px w-8 bg-gradient-to-r from-transparent to-secondary/70" />
              <span className="eyebrow text-[10px] text-secondary text-glow-magenta">Fleet Login</span>
              <span className="h-px w-8 bg-gradient-to-l from-transparent to-secondary/70" />
            </div>
          </div>

          {/* underlight strip that joins the two zones */}
          <div className="cabinet-strip" />

          {/* CONTROL PANEL — the form */}
          <div className="cabinet-panel">
          <p className="text-center text-sm text-muted-foreground mb-6">{t('auth.loginSubtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="login" className="eyebrow text-[10px] text-muted-foreground">
                {t('auth.username')}
              </Label>
              <Input
                id="login"
                type="text"
                value={formData.login}
                onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                placeholder={t('auth.username')}
                className="h-12 font-mono bg-input/70 focus-visible:ring-primary"
                data-testid="login-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="eyebrow text-[10px] text-muted-foreground">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('auth.password')}
                  className="h-12 font-mono pr-12 bg-input/70 focus-visible:ring-primary"
                  data-testid="login-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={openForgot}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  data-testid="login-forgot-password"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 font-display font-bold uppercase tracking-[0.18em] text-primary-foreground bg-primary hover:bg-primary/90 glow-amber"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.signIn')}
            </Button>
          </form>
          </div>
        </div>

        <p className="mt-5 text-center eyebrow text-[10px] text-muted-foreground/60">
          Insert credentials · Press start
        </p>
        <p className="mt-2 text-center text-[11px] text-muted-foreground/70">
          Site créé par{' '}
          <a
            href="https://www.pointpointslash.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-primary hover:text-secondary transition-colors"
          >
            PointPointSlash.com
          </a>
        </p>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">{t('auth.forgotPasswordTitle')}</DialogTitle>
            <DialogDescription>{t('auth.forgotPasswordSubtitle')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgot} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="forgot-id" className="eyebrow text-[10px] text-muted-foreground">
                {t('auth.loginOrEmail')}
              </Label>
              <Input
                id="forgot-id"
                type="text"
                value={forgotId}
                onChange={(e) => setForgotId(e.target.value)}
                placeholder={t('auth.loginOrEmail')}
                className="h-11 font-mono"
                data-testid="forgot-password-input"
                autoFocus
              />
            </div>

            {/* Captcha maison (anti-bot, sans service tiers) */}
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('auth.captchaLabel')}</Label>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-border bg-muted/40 font-mono text-sm whitespace-nowrap select-none">
                  <span>{t('auth.captchaPrompt')}</span>
                  <span className="font-bold text-primary">{captcha?.question || '…'}</span>
                  <span>= ?</span>
                </div>
                <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={loadCaptcha} title={t('auth.captchaLabel')} data-testid="forgot-captcha-refresh">
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder={t('auth.captchaPlaceholder')}
                  className="h-11 font-mono flex-1 min-w-0"
                  data-testid="forgot-captcha-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setShowForgot(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={forgotLoading} data-testid="forgot-password-submit">
                {forgotLoading ? t('common.loading') : t('auth.sendResetLink')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
